import { Drawer, Select, Skeleton, Spin } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import styled, { createGlobalStyle } from "styled-components";
import { StyledFormTableInput } from "../../../components/atoms/Input/Input.styles";
import { PortalHeader } from "../../../components/molecules/Header";
import { portalDrawerAntHeaderCss } from "../../../components/molecules/Header/Header.styles";
import { useNotification } from "../../../hooks/useNotification";
import {
  useAddPurchaseLinesToConsignmentMutation,
  useCreateShipmentFromPurchaseAllocationMutation,
  useGetOpenPurchaseShipmentsQuery,
  usePreviewPurchaseOrderConsignmentMutation,
} from "../../../store/apiSlice/orderApiSlice";
import { getAllocationApiErrorMessage } from "../../newLayout/page/Allocation/action/createShipmentHelpers";
import { Button } from "../../newLayout/page/components/Button/Button";
import {
  SHIPMENT_REFERENCE_MAX_LENGTH,
  buildCreatePurchaseShipmentPayload,
  effectiveLineShipQty,
  filterShipmentsByDestinations,
  getEditedLineQtyText,
  lineMaxShipQty,
  previewDestinationWarehouseIds,
  shipmentMatchesSearch,
  shipmentOptionLabel,
  validateCheckedLineQty,
} from "./createShipmentFromPurchaseHelpers";

/** @const {number} Height of one preview line, used to size the hidden spacers. */
const PREVIEW_ROW_HEIGHT = 40;

/** @const {number} Extra rows drawn above and below the viewport. */
const PREVIEW_OVERSCAN = 6;

/**
 * Warehouse lines, drawing only the rows inside the scrolled viewport when
 * ``viewportHeight`` is set.
 *
 * @param {{
 *   allChecked: boolean,
 *   checkedLineIds: Set<number>,
 *   lineQuantities: Record<number, string>,
 *   lines: Array<Object>,
 *   onQtyChange: function(number, string): void,
 *   someChecked: boolean,
 *   toggleGroup: function(boolean): void,
 *   toggleLine: function(number): void,
 *   qtyTabOrder: number[],
 *   viewportHeight?: number,
 *   warehouseName: string,
 *   disabledLineIds?: Set<number>,
 *   linesDisabled?: boolean,
 * }} props
 * @return {JSX.Element}
 */
const PreviewLines = ({
  allChecked,
  checkedLineIds,
  disabledLineIds = new Set(),
  lineQuantities,
  lines,
  linesDisabled = false,
  onQtyChange,
  qtyTabOrder,
  someChecked,
  toggleGroup,
  toggleLine,
  viewportHeight = 0,
  warehouseName,
}) => {
  const [start, setStart] = useState(0);
  const scrollRef = useRef(null);
  const useWindow =
    viewportHeight > 0 && lines.length * PREVIEW_ROW_HEIGHT > viewportHeight;
  const visibleCount =
    Math.ceil(viewportHeight / PREVIEW_ROW_HEIGHT) + PREVIEW_OVERSCAN * 2;
  const rowStart = useWindow ? Math.min(start, Math.max(0, lines.length - 1)) : 0;
  const rowEnd = useWindow ? Math.min(lines.length, rowStart + visibleCount) : lines.length;

  const focusQtyInput = useCallback(
    lineId => {
      const lineIndex = lines.findIndex(line => line.line_id === lineId);
      const inThisTable = lineIndex >= 0;
      const selector = `.po-create-shipment-drawer [data-shipment-qty-line="${lineId}"]`;

      const focusElement = () => {
        const el = document.querySelector(selector);
        if (!el) {
          return;
        }
        if (!inThisTable || !useWindow) {
          el.scrollIntoView({ block: "nearest" });
        }
        el.focus();
      };

      if (inThisTable && useWindow && scrollRef.current) {
        scrollRef.current.scrollTop = lineIndex * PREVIEW_ROW_HEIGHT;
        requestAnimationFrame(() => {
          requestAnimationFrame(focusElement);
        });
        return;
      }

      requestAnimationFrame(focusElement);
    },
    [lines, useWindow],
  );

  const handleQtyKeyDown = (event, lineId) => {
    if (event.key !== "Tab") {
      return;
    }

    const currentIndex = qtyTabOrder.indexOf(lineId);
    if (currentIndex < 0) {
      return;
    }

    const nextIndex = event.shiftKey ? currentIndex - 1 : currentIndex + 1;
    if (nextIndex < 0 || nextIndex >= qtyTabOrder.length) {
      return;
    }

    event.preventDefault();
    focusQtyInput(qtyTabOrder[nextIndex]);
  };

  const onScroll = event => {
    const next = Math.max(
      0,
      Math.floor(event.currentTarget.scrollTop / PREVIEW_ROW_HEIGHT) - PREVIEW_OVERSCAN,
    );
    setStart(current => (current === next ? current : next));
  };

  return (
    <div
      ref={scrollRef}
      onScroll={useWindow ? onScroll : undefined}
      style={
        useWindow
          ? { maxHeight: viewportHeight, overflowY: "auto", minHeight: 0 }
          : undefined
      }
    >
      <LineTable>
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                checked={allChecked}
                ref={input => {
                  if (input) {
                    input.indeterminate = someChecked && !allChecked;
                  }
                }}
                onChange={event => toggleGroup(event.target.checked)}
                disabled={linesDisabled}
                aria-label={`Select all lines for ${warehouseName}`}
              />
            </th>
            <th>#</th>
            <th>PO</th>
            <th>Product</th>
            <th>Supplier</th>
            <th>Status</th>
            <th>Qty</th>
            <th>ETA</th>
          </tr>
        </thead>
        <tbody>
          {useWindow && rowStart > 0 && (
            <tr aria-hidden="true">
              <td colSpan={8} style={{ height: rowStart * PREVIEW_ROW_HEIGHT, padding: 0 }} />
            </tr>
          )}
          {lines.slice(rowStart, rowEnd).map((line, sliceIndex) => {
            const maxQty = lineMaxShipQty(line);
            const qtyText = getEditedLineQtyText(lineQuantities, line.line_id, line);
            const isLineDisabled = linesDisabled || disabledLineIds.has(line.line_id);
            const isChecked = !isLineDisabled && checkedLineIds.has(line.line_id);
            const qtyValid = !isChecked || validateCheckedLineQty(qtyText, maxQty).valid;

            return (
              <tr
                key={line.line_id}
                style={useWindow ? { height: PREVIEW_ROW_HEIGHT } : undefined}
              >
                <td>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={isLineDisabled}
                    onChange={() => toggleLine(line.line_id)}
                    aria-label={`Include ${line.order_number} ${line.product_number}`}
                  />
                </td>
                <td>
                  {line.line_number ??
                    line.line_no ??
                    (useWindow ? rowStart + sliceIndex + 1 : sliceIndex + 1)}
                </td>
                <td>{line.order_number}</td>
                <td>
                  {line.product_number}
                  {line.product_name ? ` · ${line.product_name}` : ""}
                </td>
                <td>{line.supplier_name || "—"}</td>
                <td>{line.status}</td>
                <td>
                  <QtyInput
                    type="text"
                    inputMode="decimal"
                    data-shipment-qty-line={line.line_id}
                    aria-label={`Ship quantity for ${line.product_number}`}
                    aria-invalid={!qtyValid}
                    disabled={!isChecked}
                    tabIndex={isChecked ? 0 : -1}
                    value={qtyText}
                    $invalid={!qtyValid}
                    onChange={event => onQtyChange(line.line_id, event.target.value)}
                    onKeyDown={event => handleQtyKeyDown(event, line.line_id)}
                  />
                </td>
                <td>{formatEta(line.eta)}</td>
              </tr>
            );
          })}
          {useWindow && lines.length > rowEnd && (
            <tr aria-hidden="true">
              <td
                colSpan={8}
                style={{ height: (lines.length - rowEnd) * PREVIEW_ROW_HEIGHT, padding: 0 }}
              />
            </tr>
          )}
        </tbody>
      </LineTable>
    </div>
  );
};

const DrawerTheme = createGlobalStyle`
  .po-create-shipment-drawer .ant-drawer-content {
    background: #fff;
    border-left: 1px solid var(--PEARL, #ecf1f4);
    color: var(--GREY, #4a4a68);
    font-family: Inter, sans-serif;
  }
  .po-create-shipment-drawer .ant-drawer-header {
    ${portalDrawerAntHeaderCss}
  }
  .po-create-shipment-drawer .ant-drawer-title {
    font-family: inherit;
    width: 100%;
  }
  .po-create-shipment-drawer .ant-drawer-body {
    display: flex;
    flex-direction: column;
    padding: 0;
    overflow: hidden;
  }
  .po-add-shipment-dropdown .ant-select-item {
    font-family: Inter, sans-serif;
    font-size: 14px;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const Scroll = styled.div`
  flex: 1;
  overflow: auto;
  padding: 20px 24px;
  min-height: 0;
`;

const ReviewMain = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  padding: 20px 24px;
`;

const SummaryBar = styled.div`
  padding: 14px 24px;
  border-bottom: 1px solid var(--PEARL, #ecf1f4);
  background: #f8fafb;
  font-size: 14px;
  line-height: 1.5;
  flex-shrink: 0;
`;

const WarehouseCard = styled.section`
  border: 1px solid var(--PEARL, #ecf1f4);
  border-radius: 8px;
  margin-bottom: ${({ $fill }) => ($fill ? "0" : "16px")};
  overflow: hidden;
  display: ${({ $fill }) => ($fill ? "flex" : "block")};
  flex: ${({ $fill }) => ($fill ? "1" : "none")};
  flex-direction: column;
  min-height: 0;
`;

const WarehouseHeader = styled.div`
  padding: 12px 16px;
  background: #f3f6f8;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
`;

const LinesViewport = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const LineTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    padding: 10px 12px;
    border-top: 1px solid var(--PEARL, #ecf1f4);
    text-align: left;
    vertical-align: middle;
  }

  th {
    font-weight: 600;
    color: #6b7280;
    background: #fff;
  }

  td:first-child,
  th:first-child {
    width: 40px;
    text-align: center;
  }

  td:nth-child(6) {
    width: 96px;
  }
`;

const QtyInput = styled(StyledFormTableInput)`
  border-color: ${({ $invalid }) => ($invalid ? "#cf324a" : undefined)};
  height: 32px;
  padding: 4px 8px;
`;

const SkippedSection = styled.div`
  margin-top: 8px;
  padding-top: 16px;
  border-top: 1px dashed var(--PEARL, #ecf1f4);
  flex-shrink: 0;

  h4 {
    margin: 0 0 8px;
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
  }

  ul {
    margin: 0;
    padding-left: 18px;
    font-size: 13px;
    color: #6b7280;
  }
`;

const Footer = styled.div`
  padding: 16px 24px;
  border-top: 1px solid var(--PEARL, #ecf1f4);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background: #fff;
  flex-shrink: 0;
`;

const ErrorBlock = styled.div`
  padding: 24px;
  text-align: center;

  p {
    margin-bottom: 16px;
    color: #4a4a68;
  }
`;

const SuccessList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    padding: 12px 0;
    border-bottom: 1px solid var(--PEARL, #ecf1f4);
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }
`;

const HeaderShipmentSelect = styled(Select)`
  width: min(420px, 36vw);

  &.ant-select-single .ant-select-selector {
    background: var(--CLOUD, #fcfdfe);
    border: 1px solid var(--LIGHT-ASH, #b9b9c6);
    border-radius: 4px;
    height: 36px;
    padding: 0 11px;
  }

  &.ant-select-single .ant-select-selection-item,
  &.ant-select-single .ant-select-selection-placeholder {
    font-size: 14px;
    line-height: 34px;
  }

  &.ant-select-single .ant-select-selection-item {
    color: var(--GREY, #4a4a68);
  }

  &.ant-select-single .ant-select-selection-placeholder {
    color: var(--ASH, #676879);
  }
`;

const HeaderShipmentNameInput = styled.input`
  background: var(--CLOUD, #fcfdfe);
  border-radius: 4px;
  border: 1px solid var(--LIGHT-ASH, #b9b9c6);
  color: var(--GREY, #4a4a68);
  font-size: 14px;
  font-weight: 400;
  height: 36px;
  line-height: 22px;
  padding: 0 12px;
  width: min(240px, 28vw);

  &::placeholder {
    color: var(--ASH, #676879);
    opacity: 0.65;
  }
`;

const formatEta = eta => {
  if (!eta) {
    return "—";
  }
  return eta.length >= 10 ? eta.slice(0, 10) : eta;
};

const collectLineIdsFromGroups = groups =>
  (groups || []).flatMap(group => (group.lines || []).map(line => line.line_id));

const buildLinesById = groups => {
  const map = {};
  (groups || []).forEach(group => {
    (group.lines || []).forEach(line => {
      map[line.line_id] = line;
    });
  });
  return map;
};

const countShippableGroups = groups =>
  (groups || []).filter(group => (group.lines || []).length > 0).length;

/**
 * Review drawer: preview consignments grouped by warehouse, confirm create.
 */
export const CreateShipmentReviewDrawer = ({
  isOpen,
  mode = "create",
  onClose,
  orders = [],
  lineIds,
  setSelectedOrder,
  onSuccess,
}) => {
  const notify = useNotification();
  const isAddMode = mode === "add";
  const [preview] = usePreviewPurchaseOrderConsignmentMutation();
  const [createShipment, { isLoading: isCreating }] =
    useCreateShipmentFromPurchaseAllocationMutation();
  const [addPurchaseLines, { isLoading: isAdding }] =
    useAddPurchaseLinesToConsignmentMutation();
  const isSubmitting = isAddMode ? isAdding : isCreating;

  const [phase, setPhase] = useState("loading");
  const [previewData, setPreviewData] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [checkedLineIds, setCheckedLineIds] = useState(() => new Set());
  const [lineQuantities, setLineQuantities] = useState({});
  const [shipmentReference, setShipmentReference] = useState("");
  const [selectedShipmentId, setSelectedShipmentId] = useState("");
  const [createdConsignments, setCreatedConsignments] = useState([]);
  const [linesViewportHeight, setLinesViewportHeight] = useState(0);
  const linesViewportRef = useRef(null);

  const shippableGroupCount = useMemo(
    () => countShippableGroups(previewData?.groups),
    [previewData],
  );
  const singleWarehouseLayout = shippableGroupCount === 1;

  useEffect(() => {
    if (!singleWarehouseLayout || phase !== "review") {
      setLinesViewportHeight(0);
      return undefined;
    }

    const element = linesViewportRef.current;
    if (!element) {
      return undefined;
    }

    const updateHeight = () => {
      const next = Math.max(0, element.clientHeight);
      setLinesViewportHeight(current => (current === next ? current : next));
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);
    return () => observer.disconnect();
  }, [phase, singleWarehouseLayout, previewData]);

  const previewSelectionKey = useMemo(() => {
    if (lineIds?.length) {
      const sorted = [...lineIds].map(Number).sort((a, b) => a - b);
      return `lines:${sorted.join(",")}`;
    }
    if (orders?.length) {
      return `orders:${orders.map(order => order.id).join(",")}`;
    }
    return "";
  }, [lineIds, orders]);

  const buildPreviewBody = useCallback(() => {
    if (previewSelectionKey.startsWith("lines:")) {
      return {
        purchase_order_line_ids: previewSelectionKey
          .slice(6)
          .split(",")
          .filter(Boolean)
          .map(Number),
      };
    }
    if (previewSelectionKey.startsWith("orders:")) {
      return {
        purchase_order_ids: previewSelectionKey
          .slice(7)
          .split(",")
          .filter(Boolean)
          .map(Number),
      };
    }
    return null;
  }, [previewSelectionKey]);

  const loadPreview = useCallback(async () => {
    const previewBody = buildPreviewBody();
    if (!previewBody) {
      return;
    }

    setPhase("loading");
    setPreviewError("");
    setPreviewData(null);
    setCreatedConsignments([]);
    setLineQuantities({});
    setShipmentReference("");
    setSelectedShipmentId("");

    try {
      const result = await preview(previewBody).unwrap();
      setPreviewData(result);
      setCheckedLineIds(new Set(collectLineIdsFromGroups(result.groups)));
      setPhase("review");
    } catch (error) {
      const message = getAllocationApiErrorMessage(
        error,
        "Could not load shipment preview. Please try again.",
      );
      setPreviewError(message);
      setPhase("error");
      notify({ type: "error", message });
    }
  }, [buildPreviewBody, notify, preview]);

  useEffect(() => {
    if (!isOpen || !previewSelectionKey) {
      return;
    }
    loadPreview();
  }, [isOpen, previewSelectionKey, loadPreview]);

  const destinationWarehouseIds = useMemo(
    () => previewDestinationWarehouseIds(previewData?.groups),
    [previewData],
  );
  const { data: shipmentOptions = [], isFetching: isLoadingShipments } =
    useGetOpenPurchaseShipmentsQuery(destinationWarehouseIds.join(","), {
      skip: !isAddMode || phase !== "review" || destinationWarehouseIds.length === 0,
    });
  const openShipments = useMemo(
    () => filterShipmentsByDestinations(shipmentOptions, destinationWarehouseIds),
    [destinationWarehouseIds, shipmentOptions],
  );
  const selectedShipment = useMemo(
    () =>
      openShipments.find(item => String(item.id) === String(selectedShipmentId)) || null,
    [openShipments, selectedShipmentId],
  );
  const selectedWarehouseId = selectedShipment?.to_warehouse_id ?? null;
  const disabledLineIds = useMemo(() => {
    if (!isAddMode || selectedWarehouseId == null) {
      return new Set();
    }
    const ids = [];
    (previewData?.groups || []).forEach(group => {
      if (group.warehouse_id === selectedWarehouseId) {
        return;
      }
      (group.lines || []).forEach(line => {
        ids.push(line.line_id);
      });
    });
    return new Set(ids);
  }, [isAddMode, previewData, selectedWarehouseId]);

  useEffect(() => {
    if (!isAddMode || phase !== "review") {
      return;
    }
    if (openShipments.length === 1) {
      setSelectedShipmentId(String(openShipments[0].id));
      return;
    }
    setSelectedShipmentId(current =>
      openShipments.some(item => String(item.id) === String(current)) ? current : "",
    );
  }, [isAddMode, openShipments, phase]);

  useEffect(() => {
    if (!disabledLineIds.size) {
      return;
    }
    setCheckedLineIds(current => {
      const next = new Set([...current].filter(id => !disabledLineIds.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [disabledLineIds]);

  const linesById = useMemo(
    () => buildLinesById(previewData?.groups),
    [previewData],
  );

  const qtyTabOrder = useMemo(() => {
    const order = [];
    (previewData?.groups || []).forEach(group => {
      (group.lines || []).forEach(line => {
        if (checkedLineIds.has(line.line_id)) {
          order.push(line.line_id);
        }
      });
    });
    return order;
  }, [checkedLineIds, previewData]);

  const onQtyChange = (lineId, value) => {
    setLineQuantities(prev => ({ ...prev, [lineId]: value }));
  };

  const toggleLine = lineId => {
    if (disabledLineIds.has(lineId)) {
      return;
    }
    setCheckedLineIds(prev => {
      const next = new Set(prev);
      if (next.has(lineId)) {
        next.delete(lineId);
      } else {
        next.add(lineId);
      }
      return next;
    });
  };

  const toggleGroup = (group, checked) => {
    const ids = (group.lines || [])
      .map(line => line.line_id)
      .filter(id => !disabledLineIds.has(id));
    setCheckedLineIds(prev => {
      const next = new Set(prev);
      ids.forEach(id => {
        if (checked) {
          next.add(id);
        } else {
          next.delete(id);
        }
      });
      return next;
    });
  };

  const hasInvalidCheckedQty = useMemo(() => {
    for (const lineId of checkedLineIds) {
      if (disabledLineIds.has(lineId)) {
        continue;
      }
      const line = linesById[lineId];
      if (!line) {
        continue;
      }
      const qtyText = getEditedLineQtyText(lineQuantities, lineId, line);
      if (!validateCheckedLineQty(qtyText, lineMaxShipQty(line)).valid) {
        return true;
      }
    }
    return false;
  }, [checkedLineIds, disabledLineIds, lineQuantities, linesById]);

  const summary = useMemo(() => {
    const groups = previewData?.groups || [];
    let shipmentCount = 0;
    let lineCount = 0;
    let totalQty = 0;

    groups.forEach(group => {
      const lines = (group.lines || []).filter(
        line => checkedLineIds.has(line.line_id) && !disabledLineIds.has(line.line_id),
      );
      if (!lines.length) {
        return;
      }
      shipmentCount += 1;
      lineCount += lines.length;
      lines.forEach(line => {
        totalQty += effectiveLineShipQty(lineQuantities, line.line_id, line);
      });
    });

    return { shipmentCount, lineCount, totalQty };
  }, [checkedLineIds, disabledLineIds, lineQuantities, previewData]);

  const handleCreate = async () => {
    const ids = [...checkedLineIds].filter(id => !disabledLineIds.has(id));
    if (!ids.length || isSubmitting || hasInvalidCheckedQty) {
      return;
    }
    if (isAddMode && !selectedShipment) {
      return;
    }

    try {
      const body = buildCreatePurchaseShipmentPayload({
        ids,
        lineQuantities,
        linesById,
        reference: isAddMode ? "" : shipmentReference,
      });
      if (isAddMode) {
        const res = await addPurchaseLines({
          consignment_id: selectedShipment.id,
          ids: body.ids,
          quantities: body.quantities,
        }).unwrap();
        setCreatedConsignments([res]);
        setPhase("success");
        notify({ type: "success", message: "Lines added to the shipment." });
        return;
      }
      const res = await createShipment(body).unwrap();
      if (!Array.isArray(res) || !res.length) {
        notify({
          type: "error",
          message:
            "No shipment was created from the selected lines. Check facility and " +
            "allocated quantity, then try again.",
        });
        return;
      }
      setCreatedConsignments(res);
      setPhase("success");
      notify({ type: "success", message: "Shipment created successfully." });
    } catch (error) {
      notify({
        type: "error",
        message: getAllocationApiErrorMessage(error),
      });
    }
  };

  const handleDone = () => {
    setSelectedOrder?.([]);
    onSuccess?.();
    onClose();
  };

  const title =
    phase === "success"
      ? isAddMode
        ? "Added to shipment"
        : "Shipments created"
      : isAddMode
        ? "Add to shipment"
        : "Create shipment";

  const canCreate =
    summary.lineCount > 0 &&
    !isSubmitting &&
    !hasInvalidCheckedQty &&
    (!isAddMode || Boolean(selectedShipment));

  const renderWarehouseGroups = (fillSingleGroup = false) =>
    previewData?.groups?.map(group => {
      const groupLines = group.lines || [];
      const linesDisabled =
        isAddMode && selectedWarehouseId != null && group.warehouse_id !== selectedWarehouseId;
      const checkedInGroup = groupLines.filter(
        line => checkedLineIds.has(line.line_id) && !disabledLineIds.has(line.line_id),
      );
      const groupQty = checkedInGroup.reduce(
        (sum, line) => sum + effectiveLineShipQty(lineQuantities, line.line_id, line),
        0,
      );
      const allChecked =
        groupLines.length > 0 && groupLines.every(line => checkedLineIds.has(line.line_id));
      const someChecked = groupLines.some(line => checkedLineIds.has(line.line_id));

      if (!groupLines.length) {
        return null;
      }

      const fill = fillSingleGroup && singleWarehouseLayout;

      return (
        <WarehouseCard key={group.warehouse_id} $fill={fill}>
          <WarehouseHeader>
            <span>
              {group.warehouse_name || "Destination warehouse"}
              {linesDisabled ? " · Different destination" : ""}
            </span>
            <span>
              {checkedInGroup.length} / {groupLines.length} lines · {groupQty} qty
            </span>
          </WarehouseHeader>
          {fill ? (
            <LinesViewport ref={linesViewportRef}>
              <PreviewLines
                allChecked={allChecked}
                checkedLineIds={checkedLineIds}
                disabledLineIds={disabledLineIds}
                lineQuantities={lineQuantities}
                lines={groupLines}
                linesDisabled={linesDisabled}
                onQtyChange={onQtyChange}
                qtyTabOrder={qtyTabOrder}
                someChecked={someChecked}
                toggleGroup={checked => toggleGroup(group, checked)}
                toggleLine={toggleLine}
                viewportHeight={linesViewportHeight}
                warehouseName={group.warehouse_name}
              />
            </LinesViewport>
          ) : (
            <PreviewLines
              allChecked={allChecked}
              checkedLineIds={checkedLineIds}
              disabledLineIds={disabledLineIds}
              lineQuantities={lineQuantities}
              lines={groupLines}
              linesDisabled={linesDisabled}
              onQtyChange={onQtyChange}
              qtyTabOrder={qtyTabOrder}
              someChecked={someChecked}
              toggleGroup={checked => toggleGroup(group, checked)}
              toggleLine={toggleLine}
              warehouseName={group.warehouse_name}
            />
          )}
        </WarehouseCard>
      );
    });

  const headerSupplementary =
    phase === "review" ? (
      isAddMode ? (
        <>
          <label className="visually-hidden" htmlFor="po-add-shipment-select">
            Shipment
          </label>
          <HeaderShipmentSelect
            id="po-add-shipment-select"
            showSearch
            allowClear
            popupClassName="po-add-shipment-dropdown"
            optionFilterProp="label"
            filterOption={(input, option) =>
              shipmentMatchesSearch(
                openShipments.find(item => String(item.id) === String(option?.value)),
                input,
              )
            }
            placeholder={
              isLoadingShipments
                ? "Loading shipments…"
                : openShipments.length === 0
                  ? "No open shipments for this destination"
                  : "Search shipment name or number"
            }
            value={selectedShipmentId || undefined}
            disabled={isLoadingShipments || openShipments.length === 0}
            onChange={value => setSelectedShipmentId(value ? String(value) : "")}
            options={openShipments.map(shipment => ({
              value: String(shipment.id),
              label: shipmentOptionLabel(shipment),
            }))}
          />
        </>
      ) : (
        <>
          <label className="visually-hidden" htmlFor="po-create-shipment-reference">
            Shipment Name
          </label>
          <HeaderShipmentNameInput
            id="po-create-shipment-reference"
            type="text"
            maxLength={SHIPMENT_REFERENCE_MAX_LENGTH}
            placeholder="Shipment Name"
            value={shipmentReference}
            onChange={event => setShipmentReference(event.target.value)}
          />
        </>
      )
    ) : null;

  return (
    <>
      <DrawerTheme />
      <Drawer
        open={isOpen}
        title={
          <PortalHeader
            title={title}
            supplementary={headerSupplementary}
            onClose={onClose}
          />
        }
        rootClassName="po-create-shipment-drawer"
        placement="right"
        width="min(1240px, calc(100vw - 32px))"
        styles={{
          header: { padding: 0 },
          body: { padding: 0, overflow: "hidden", display: "flex", flexDirection: "column" },
          mask: { backgroundColor: "rgba(0, 0, 0, 0.3)", backdropFilter: "blur(1px)" },
        }}
        onClose={onClose}
        closable={false}
        maskClosable={false}
        keyboard
        destroyOnClose
      >
        <Body>
          {phase === "loading" && (
            <Scroll>
              <Skeleton active paragraph={{ rows: 8 }} />
            </Scroll>
          )}

          {phase === "error" && (
            <ErrorBlock>
              <p>{previewError}</p>
              <Button btnName="Retry" handler={loadPreview} classOfBtn="submitBtn" />
            </ErrorBlock>
          )}

          {phase === "review" && previewData && (
            <>
              <SummaryBar>
                {summary.shipmentCount === 0 ? (
                  <span>
                    {isAddMode
                      ? "No lines selected. Check at least one line for this shipment's destination."
                      : "No lines selected. Check at least one line to create a shipment."}
                  </span>
                ) : isAddMode ? (
                  <span>
                    <strong>{summary.lineCount}</strong>{" "}
                    {summary.lineCount === 1 ? "line" : "lines"} ·{" "}
                    <strong>{summary.totalQty}</strong> total qty
                  </span>
                ) : (
                  <span>
                    <strong>{summary.shipmentCount}</strong>{" "}
                    {summary.shipmentCount === 1 ? "shipment" : "shipments"} ·{" "}
                    <strong>{summary.lineCount}</strong>{" "}
                    {summary.lineCount === 1 ? "line" : "lines"} ·{" "}
                    <strong>{summary.totalQty}</strong> total qty
                  </span>
                )}
              </SummaryBar>

              {singleWarehouseLayout ? (
                <ReviewMain>
                  {!previewData.groups?.length && !previewData.skipped_lines?.length && (
                    <p>No shippable lines were found in the selection.</p>
                  )}
                  {renderWarehouseGroups(true)}
                  {previewData.skipped_lines?.length > 0 && (
                    <SkippedSection>
                      <h4>Not included</h4>
                      <ul>
                        {previewData.skipped_lines.map(line => (
                          <li key={line.line_id}>
                            {line.order_number} ({line.product_number}) — {line.reason}
                          </li>
                        ))}
                      </ul>
                    </SkippedSection>
                  )}
                </ReviewMain>
              ) : (
                <Scroll>
                  {!previewData.groups?.length && !previewData.skipped_lines?.length && (
                    <p>No shippable lines were found in the selection.</p>
                  )}
                  {renderWarehouseGroups(false)}
                  {previewData.skipped_lines?.length > 0 && (
                    <SkippedSection>
                      <h4>Not included</h4>
                      <ul>
                        {previewData.skipped_lines.map(line => (
                          <li key={line.line_id}>
                            {line.order_number} ({line.product_number}) — {line.reason}
                          </li>
                        ))}
                      </ul>
                    </SkippedSection>
                  )}
                </Scroll>
              )}

              <Footer>
                <Button btnName="Cancel" handler={onClose} classOfBtn="cancelBtn" />
                <Button
                  btnName={
                    isSubmitting
                      ? isAddMode
                        ? "Adding…"
                        : "Creating…"
                      : isAddMode
                        ? "Add to shipment"
                        : "Create shipment"
                  }
                  handler={handleCreate}
                  classOfBtn="submitBtn"
                  isDisabledButton={!canCreate || isSubmitting}
                />
              </Footer>
            </>
          )}

          {phase === "success" && (
            <>
              <Scroll>
                <p style={{ marginTop: 0 }}>
                  {isAddMode
                    ? "The selected lines were added to the shipment."
                    : createdConsignments.length === 1
                      ? "Your shipment is ready."
                      : `${createdConsignments.length} shipments were created.`}
                </p>
                <SuccessList>
                  {createdConsignments.map(item => (
                    <li key={item.id}>
                      <span>
                        <strong>{item.reference || item.name}</strong>
                      </span>
                      <Link to={`/shipment/po-to/${item.id}`}>Open</Link>
                    </li>
                  ))}
                </SuccessList>
              </Scroll>
              <Footer>
                <Button btnName="Done" handler={handleDone} classOfBtn="submitBtn" />
              </Footer>
            </>
          )}

          {isSubmitting && phase === "review" && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(255,255,255,0.6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Spin size="large" />
            </div>
          )}
        </Body>
      </Drawer>
    </>
  );
};
